import { create } from 'zustand';

import { withDevtools } from './middleware-utils';

import type { Recipient } from '../hooks/client';

/**
 * Transfer progress states
 */
export type TransferStatus =
  | 'idle'
  | 'validating'
  | 'executing'
  | 'polling'
  | 'completed'
  | 'failed';

export interface TransferProgress {
  id: string;
  recipient: Recipient; // Recipient type
  amount: number;
  concept: string;
  status: TransferStatus;
  txnId?: string;
  orderId?: string; // for SPEI (payment-order)
  startedAt: number;
  completedAt?: number;
  error?: {
    code: string;
    message: string;
    retryable: boolean;
  };
}

export interface TransferError {
  code: string;
  message: string;
  retryable: boolean;
}

interface TransfersStore {
  // State
  transfers: Map<string, TransferProgress>;
  selectedRecipient: Recipient | null; // Recipient type
  lastError: TransferError | null;
  stats: {
    totalSent: number;
    totalFailed: number;
    lastId?: string;
  };

  // Actions
  initTransfer: (id: string, recipient: Recipient, amount: number, concept: string) => void;
  markAsValidating: (id: string) => void;
  markAsExecuting: (id: string) => void;
  markAsPolling: (id: string, orderId: string) => void;
  markAsCompleted: (id: string, txnId: string) => void;
  markAsFailed: (id: string, error: TransferError) => void;
  selectRecipient: (recipient: Recipient | null) => void;
  clearError: () => void;
  clearTransfer: (id: string) => void;
}

export const useTransfersStore = create<TransfersStore>()(
  withDevtools('Transfers', set => ({
    transfers: new Map(),
    selectedRecipient: null,
    lastError: null,
    stats: {
      totalSent: 0,
      totalFailed: 0,
    },

    initTransfer: (id, recipient, amount, concept) =>
      set(state => {
        const transfers = new Map(state.transfers);
        transfers.set(id, {
          id,
          recipient,
          amount,
          concept,
          status: 'idle',
          startedAt: Date.now(),
        });
        return { transfers };
      }),

    markAsValidating: id =>
      set(state => {
        const transfers = new Map(state.transfers);
        const transfer = transfers.get(id);
        if (transfer) {
          transfers.set(id, { ...transfer, status: 'validating' });
        }
        return { transfers };
      }),

    markAsExecuting: id =>
      set(state => {
        const transfers = new Map(state.transfers);
        const transfer = transfers.get(id);
        if (transfer) {
          transfers.set(id, { ...transfer, status: 'executing' });
        }
        return { transfers };
      }),

    markAsPolling: (id, orderId) =>
      set(state => {
        const transfers = new Map(state.transfers);
        const transfer = transfers.get(id);
        if (transfer) {
          transfers.set(id, { ...transfer, status: 'polling', orderId });
        }
        return { transfers };
      }),

    markAsCompleted: (id, txnId) =>
      set(state => {
        const transfers = new Map(state.transfers);
        const transfer = transfers.get(id);
        if (transfer) {
          transfers.set(id, {
            ...transfer,
            status: 'completed',
            txnId,
            completedAt: Date.now(),
          });
          return {
            transfers,
            stats: {
              ...state.stats,
              totalSent: state.stats.totalSent + 1,
              lastId: id,
            },
          };
        }
        return { transfers };
      }),

    markAsFailed: (id, error) =>
      set(state => {
        const transfers = new Map(state.transfers);
        const transfer = transfers.get(id);
        if (transfer) {
          transfers.set(id, {
            ...transfer,
            status: 'failed',
            error,
            completedAt: Date.now(),
          });
          return {
            transfers,
            lastError: error,
            stats: {
              ...state.stats,
              totalFailed: state.stats.totalFailed + 1,
            },
          };
        }
        return { transfers };
      }),

    selectRecipient: recipient => set({ selectedRecipient: recipient }),

    clearError: () => set({ lastError: null }),

    clearTransfer: id =>
      set(state => {
        const transfers = new Map(state.transfers);
        transfers.delete(id);
        return { transfers };
      }),
  }))
);
